import React, { Fragment } from 'react';
import { removeTypenameGeneric } from '@ssg/common/Helpers/typenameHelper';
import { useStorageState } from '@ssg/common/Hooks/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import { assessmentPoints } from '@ssg/common/Configurations/AssesmentPoints';
import { inspectionAssesmentSignatureLines } from '@ssg/common/Configurations/InspectionAssesmentSignatureLines';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { AssesmentColor, AssesmentInput, DeviationColor, DeviationInput, GetMobileV2DrivingSlip_drivingSlip_case, SignatureInputType } from 'GQL';
import { stringToByteArray } from '@ssg/common/Helpers/inputFileHelper';
import { ESDHFileFragment } from '@ssg/common/GraphQL';
import { formatDateForInput } from '@ssg/common/Helpers/dateToDateOnlyString';
import { checkFilesExist, tryGetFile } from './checkFilesExist';
import { ApiMutationKey, useCreateInspectionAssesmentReport } from 'Store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import FormFieldHeader from '@ssg/common/Components/FormFieldHeader';
import Datepicker from '@ssg/common/Components/Datepicker';
import TextInput from './Inputs/TextInput';
import TextAreaInput from './Inputs/TextAreaInput';
import Button from 'Components/Button';
import ReportStaticInfo from './ReportStaticInfo';
import Radio from '@ssg/common/Components/Radio';
import BasePage from 'Components/Layout/BasePage';
import SignatureInput from './Inputs/SignatureInput';
import useDrivingSlipImages from './useDrivingSlipImages';

interface Props {
	drivingSlipCase: GetMobileV2DrivingSlip_drivingSlip_case;
	caseNo: string;
	drivingSlipId: string;
}
interface DeviationError {
	assesmentId: number;
	key: keyof DeviationInput;
}

const colorSelectionOptions: SelectOption[] = [
	{ label: 'reports.asbestos.green', value: AssesmentColor.GREEN },
	{ label: 'reports.asbestos.yellow', value: AssesmentColor.YELLOW },
	{ label: 'reports.asbestos.red', value: AssesmentColor.RED },
];

const DeviationColorOptions: SelectOption[] = [
	{ label: 'reports.asbestos.yellow', value: DeviationColor.YELLOW },
	{ label: 'reports.asbestos.red', value: DeviationColor.RED },
];

const InspectionAssesmentReportForm: React.FC<Props> = ({ drivingSlipCase, caseNo, drivingSlipId }): React.ReactElement => {
	const { t } = useTranslation();
	const history = useHistory();

	const saveButtonReportsFlag = useFlag(FeatureFlagEnums.SAVE_BUTTON_REPORTS);

	React.useEffect(() => {
		const formData = removeTypenameGeneric(drivingSlipCase.caseAsbestosReports?.inspectionAssesmentFormData);
		if (formData) {
			if (formData.assesments !== null) {
				const sanitizedAssesments: AssesmentInput[] = removeTypenameGeneric(formData.assesments);
				setAssesments(sanitizedAssesments as AssesmentInput[]);
			}

			if (formData.signatures !== null) {
				const sanitizedSignatures = removeTypenameGeneric(formData.signatures);

				const nonNullFiles = sanitizedSignatures
					.filter(signature => signature !== null && signature !== undefined && signature.signatureDetails !== null && signature.signatureDetails !== undefined)
					.map(signature => ({ fileName: signature!.signatureDetails!.fileName }));

				const existingFiles = checkFilesExist(nonNullFiles, images);

				const singatureList = sanitizedSignatures.map(signature => {
					if (signature?.signatureDetails?.fileName) {
						const fileExists = existingFiles.some(file => file.fileName === signature!.signatureDetails!.fileName);
						if (!fileExists) {
							signature.signatureDate = null;
							signature.signatureDetails = null;
						}
					}
					return signature;
				});
				setSignatures(singatureList as SignatureInputType[]);
				const sigFiles: (ESDHFileFragment | null)[] = [];
				sanitizedSignatures.forEach((signature, index) => {
					const fileExists = existingFiles.some(file => file.fileName === signature?.signatureDetails?.fileName);
					if (!fileExists || signature?.signatureDetails?.fileName === undefined) {
						sigFiles.push(null);
					} else {
						const file = tryGetFile(signature?.signatureDetails?.fileName, images);
						sigFiles.push(file);
					}
				});
				setSignatureFiles(sigFiles);
			}
		}
		setActiveTab(assesments.find(a => a.deviation !== undefined)?.id ?? 0);
	}, [drivingSlipCase.caseAsbestosReports?.inspectionAssesmentFormData]);

	const useLocalStorage = <T,>(name: string, initialValue: T) => useStorageState<T>(window.localStorage, `${drivingSlipCase.id}-inspection-assesment${name}`, initialValue);

	const [assesments, setAssesments] = useLocalStorage<AssesmentInput[]>('inspectionAssesmentQuestions', populateEmptyLocalstorage());
	const [activeTab, setActiveTab] = React.useState<number>(0);
	const setColor = (index: number, value: AssesmentColor) => {
		setAssesments(oldAssesments => oldAssesments.map(assesment => (assesment.id === index ? { ...assesment, color: value } : assesment)));
	};
	const setComment = (index: number, value: string) => {
		setAssesments(oldAssesments => oldAssesments.map(assesment => (assesment.id === index ? { ...assesment, comment: value } : assesment)));
	};
	const [signatures, setSignatures] = useLocalStorage<SignatureInputType[]>('signatures', populateSignaturesInLocaleStorage());
	const [signatureFiles, setSignatureFiles] = React.useState<(ESDHFileFragment | null)[] | undefined | null>();
	const [fileErrors, setFileErrors] = React.useState<boolean[]>([]);
	const [deviationErrors, setDeviationError] = React.useState<DeviationError[]>([]);

	const [saved, setSaved] = React.useState(false);
	const [loadingType, setLoadingType] = React.useState<'CREATE' | 'SAVE'>('CREATE');
	const { upload, images } = useDrivingSlipImages(caseNo, drivingSlipId);

	const [createInspectionAssesmentReport, { loading: submitting }] = useCreateInspectionAssesmentReport();

	React.useEffect(() => {
		setSaved(false);
	}, [assesments, signatureFiles]);

	async function saveReport() {
		try {
			setLoadingType('SAVE');
			await createInspectionAssesmentReport({
				variables: {
					caseId: drivingSlipCase.id,
					formData: { assesments, signatures },
					onlySave: true,
				},
				queueOfflineMutationOptions: {
					key: ApiMutationKey.DrivingSlip_CreateInspectionAssesmentReport,
					removePreviousEntry: true,
					findPreviousEntryPredicate(variables) {
						return variables.caseId === drivingSlipCase.id;
					},
				},
			});

			setSaved(true);
		} catch (e) {
			console.error(e);
		}
	}

	return (
		<BasePage title={t('reports.reportTypes.inspectionAssesment')}>
			<IonGrid className="mb-4 text-sm">
				<IonRow>
					<ReportStaticInfo drivingSlipCase={drivingSlipCase} />
				</IonRow>
				{assessmentPoints.map(point => {
					const assesmentSelect = assesments.find(assesment => assesment.id === point.index)?.color ?? AssesmentColor.NON_SELECTED;
					const comment = assesments.find(assesment => assesment.id === point.index)?.comment ?? '';
					return (
						<Fragment key={point.index}>
							<IonRow className="mt-4 text-base font-semibold text-blue">{`Pkt. ${point.index}. ${point.assesmentPoint}`}</IonRow>
							<IonRow className="flex-col gap-3">
								<Radio
									disabled
									horizontal
									className="mt-5"
									title=""
									options={colorSelectionOptions}
									name={`question-${point.index}`}
									onChange={e => setColor(point.index, e.target.value as AssesmentColor)}
									value={assesmentSelect}
								/>
								<TextAreaInput disabled label="" placeholder={t('reports.asbestos.remark')} value={comment} onChange={value => setComment(point.index, value)} />
							</IonRow>
						</Fragment>
					);
				})}
				{assesments.find(a => a.deviation !== undefined) && (
					<>
						<div className="text-2xl mt-4 font-bold text-blue">{t('reports.asbestos.deviationTitles')}</div>
						<div className="flex flex-wrap">
							<div className="w-full">
								<ul className="mb-0 list-none flex-row flex-wrap pb-4" role="tablist">
									{assesments.map(
										(assesment, idx) =>
											assesment.deviation !== undefined &&
											assesment.deviation !== null && (
												<li key={assesment.id} className="-mb-px mr-2 flex-auto text-center last:mr-0">
													{idx === 0}{' '}
													<a
														className={
															'block rounded mt-2 px-5 py-3 text-xs font-bold uppercase leading-normal shadow-lg ' +
															(activeTab === assesment.id ? 'bg-blue-calm text-white' : 'text-blue-calm bg-white')
														}
														onClick={e => {
															e.preventDefault();
															setActiveTab(assesment.id);
														}}
														data-toggle="tab"
														href={'#link' + assesment.id}
														role="tablist"
													>
														{typeof deviationErrors.find(deviation => deviation.assesmentId === assesment.id) !== 'undefined' && (
															<FontAwesomeIcon icon={faExclamationTriangle} className="text-red mr-1" size="lg" />
														)}
														{`pkt.${assesment.id}`}
													</a>
												</li>
											),
									)}

									<li className="-mb-px mr-2 flex-auto text-center last:mr-0"></li>
								</ul>
								<div className="relative mb-6 flex w-full min-w-0 flex-col break-words rounded bg-white shadow-lg">
									<div className="flex-auto px-4 py-5">
										<div className="tab-content tab-space">
											{assesments.map(
												assesment =>
													assesment.deviation !== undefined &&
													assesment.deviation !== null && (
														<IonGrid key={assesment.id} className={activeTab === assesment.id ? 'block' : 'hidden'} id={'#link' + assesment.id}>
															<FormFieldHeader
																largeTitle
																title={`${t('reports.asbestos.deviationTitle')} - ${assessmentPoints.find(ap => ap.index === assesment.id)?.assesmentPoint}`}
															/>
															<div className="mt-4">
																<IonRow>
																	<Datepicker
																		disabled
																		title={'reports.asbestos.deviationReportDate'}
																		name={`deviationReportDate-${assesment.id}`}
																		value={assesment.deviation?.reportDate}
																		onChange={() => {
																			//
																		}}
																	/>
																</IonRow>
																<IonRow>
																	<Radio
																		disabled
																		name={`severityLevel-${assesment.id}`}
																		options={DeviationColorOptions}
																		title={'reports.asbestos.deviationSeverity'}
																		onChange={() => {
																			//
																		}}
																		value={assesment.deviation?.severityLevel}
																		horizontal
																	/>
																</IonRow>
																<IonRow>
																	<TextAreaInput
																		disabled
																		label="reports.asbestos.description"
																		value={assesment.deviation?.description}
																		onChange={() => {
																			//
																		}}
																		placeholder={'reports.asbestos.descriptionPlaceholder'}
																		autoGrowOverride={true}
																	/>
																</IonRow>
																<IonRow>
																	<TextAreaInput
																		disabled
																		label="reports.asbestos.cause"
																		value={assesment.deviation?.cause}
																		onChange={() => {
																			//
																		}}
																		placeholder={'reports.asbestos.causePlaceholder'}
																		autoGrowOverride={true}
																	/>
																</IonRow>
																<IonRow>
																	<TextAreaInput
																		disabled
																		label="reports.asbestos.correctingAction"
																		value={assesment.deviation?.action}
																		onChange={() => {
																			//
																		}}
																		placeholder={'reports.asbestos.actionPlaceholder'}
																		autoGrowOverride={true}
																	/>
																</IonRow>
																<IonRow>
																	<TextInput
																		disabled
																		label={'reports.asbestos.actionResponsible'}
																		value={assesment.deviation?.actionResponsible}
																		onChange={() => {
																			//
																		}}
																	/>
																</IonRow>
																<IonRow>
																	<Datepicker
																		disabled
																		title={'reports.asbestos.actionDate'}
																		name={`actionDate-${assesment.id}`}
																		value={assesment.deviation?.actionDate ?? ''}
																		onChange={() => {
																			//
																		}}
																	/>
																</IonRow>
															</div>
														</IonGrid>
													),
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
				{inspectionAssesmentSignatureLines.map((entry, index) => {
					return (
						<IonRow>
							<SignatureInput
								required={false}
								label={`${t('reports.customerSignature')} ${entry}`}
								isUploading={false}
								signatureFile={signatureFiles?.[index] ?? undefined}
								onClear={() => {
									setSignatureFiles(prevFiles => {
										const updatedFiles = [...(prevFiles || [])];
										updatedFiles[index] = null;
										return updatedFiles;
									});
									setSignatures(prevFiles => {
										const updatedFiles = [...(prevFiles || [])];
										updatedFiles[index] = { index, signatureDate: null, signatureDetails: null };

										return updatedFiles;
									});
								}}
								onSubmit={async signature => {
									const fileAsBytes = stringToByteArray(signature.base64);
									const filename = `signature-${Date.now()}.${signature.extension}`;
									try {
										setFileErrors(prev => {
											const updated = [...(prev || [])];
											updated[index] = false;
											return updated;
										});
										const file = await upload({
											filename: filename,
											fileData: fileAsBytes,
											blob: signature.base64,
										});
										setSignatureFiles(prevFiles => {
											const updatedFiles = [...(prevFiles || [])];
											updatedFiles[index] = file;
											return updatedFiles;
										});
										setSignatures(prevFiles => {
											const updatedFiles = [...(prevFiles || [])];
											updatedFiles[index] = { index, signatureDate: formatDateForInput(new Date()), signatureDetails: { fileName: filename } };
											return updatedFiles;
										});
									} catch (error) {
										console.error(error);
										setFileErrors(prev => {
											const updated = [...(prev || [])];
											updated[index] = true;
											return updated;
										});
									}
								}}
								errorMessage={fileErrors[index] ? t('common.couldNotUpload') : undefined}
							/>
						</IonRow>
					);
				})}
				<IonRow>
					<IonCol size="12">
						{saveButtonReportsFlag && (
							<Button expand="block" disabled={submitting} loading={submitting && loadingType === 'SAVE'} onClick={() => saveReport()}>
								{t(saved ? 'reports.saved' : 'reports.saveReport')}
							</Button>
						)}
					</IonCol>
				</IonRow>
			</IonGrid>
		</BasePage>
	);
};

export default InspectionAssesmentReportForm;

const populateEmptyLocalstorage = (): AssesmentInput[] => {
	const initialData: AssesmentInput[] = [];
	assessmentPoints.forEach(point => {
		initialData.push({ id: point.index, comment: '', color: AssesmentColor.NON_SELECTED });
	});
	return initialData;
};

const populateSignaturesInLocaleStorage = (): SignatureInputType[] => {
	const initialData: SignatureInputType[] = [];
	inspectionAssesmentSignatureLines.forEach((line, index) => {
		initialData.push({ index, signatureDate: undefined, signatureDetails: null });
	});
	return initialData;
};
